// `/app/b/:siteId/overview` (WP1 Task 6) — replaces the T5 placeholder stub.
// Weekly mode renders the four path-engine LayerRows; setup mode renders a
// "setup — N of 4 done" progress header and the four SetupSteps as a checklist.
//
// Client-side panel analytics events (panel.overview_viewed,
// panel.next_action_clicked) were VOID'd by this task's binding amendment
// (CONSOLIDATION v2 §A3) — this screen deliberately emits nothing.
//
// WP1 Task 12: this is the ONLY screen that mounts ClaimedBanner —
// claim-finish.ts (apps/dashboard/src) redirects a successful demo claim to
// `/app/sites/:id?claimed=1`, which legacyRedirect (lib/route-map.ts)
// rewrites to `/app/b/:id/overview?claimed=1`. Every claim ERROR code lands
// on `/app?error=...` instead — app-index.tsx already renders those (T5) —
// so this screen never duplicates that handling.
//
// `?draft=<id>` — WP2 Task 8 wired the REAL consumption of this signal on
// app-index.tsx (`/app` is the documented arrival point: the marketing
// "make it yours" CTA rides the draft id through
// `/login?redirect_after=%2Fapp%3Fdraft%3D{id}`), not here. That screen
// calls `POST /api/drafts/:id/attach` and navigates straight to the new
// site's onboarding wizard on success (Task 12 retarget) — a normal
// draft-claim flow never actually renders THIS component with `?draft=`
// still in the URL. The block below stays as a harmless, honest fallback
// for the edge case of someone pasting a `?draft=` URL directly onto an
// existing site's overview route (bypassing app-index.tsx entirely):
// readArrival() just stashes the id and shows the same placeholder notice;
// nothing here calls attach or consumeArrival(), so the URL is left exactly
// as WP1 wrote it.
//
// CLAIM DOOR (Task 12): claim-finish.ts's successful-claim redirect lands
// HERE (via legacyRedirect) with `?claimed=1`. A site whose onboarding is
// NULL (never enrolled) or incomplete (`completedAt` still null) is sent
// straight to the wizard instead of this panel — `Navigate replace` to
// `locationRoutes.setup(businessId, siteId) + '?claimed=1'` so the wizard's own ClaimedBanner
// mount (setup.tsx) picks the param up unchanged. Only once onboarding is
// genuinely finished does `?claimed=1` fall through to WP1's original
// ClaimedBanner behavior below — a completed wizard is never re-entered,
// even from a bookmarked claim URL. This branch waits for `useOnboarding`
// to resolve (`kind === 'ready'`) before deciding, so it never flickers into
// the wizard and back for an already-finished site.
//
// RESUME CHIP: a site with a started-but-incomplete onboarding (state
// exists, `completedAt` null) that the path engine independently classifies
// as still in "setup mode" (WP1's own site/gbp/tracking/first-report
// checklist, unrelated to the wizard's own 4 steps) gets a one-line
// "finish your setup — N of 4 steps done" pointer back into the wizard.
// `state === null` (legacy sites, never enrolled) renders nothing — this
// screen never prompts a site that was never offered the wizard in the
// first place. No dismissal state: the chip is derived fresh from
// `useOnboarding` + the path engine every render, so it disappears on its
// own once the wizard finishes or the path engine leaves setup mode.
import { Link, Navigate, useParams } from 'react-router-dom';
import { useTenantParams } from '@/hooks/use-tenant-params';
import { useState, type ReactNode } from 'react';
import { LayerRow, type LayerStatus } from '@pageboss/ui';
import { ONBOARDING_STEPS, type OnboardingState } from '@pageboss/shared';
import { ClaimedBanner } from '@/components/claimed-banner';
import { PageContainer } from '@/components/page-container';
import { PageHeader } from '@/components/page-header';
import { readArrival } from '@/lib/arrival-context';
import { useOverviewSignals } from '@/hooks/use-overview-signals';
import { useOnboarding } from '@/hooks/use-onboarding';
import { locationRoutes } from '@/lib/route-map';
import { derivePath, type LayerAction, type PathEngineResult } from '@/lib/path-engine';

const LAYER_ROW_STATUS: Record<LayerAction['status'], LayerStatus> = {
  ok: 'done',
  attention: 'attention',
  setup: 'not_started',
};

/** Count of the wizard's 4 steps that are no longer `'pending'` (done OR
 *  skipped both count as "acted on" — same convention as `countSkipped` in
 *  packages/shared/src/onboarding.ts). Used only for the resume chip's "N of
 *  4 steps done" copy. */
function stepsActedOnCount(state: OnboardingState): number {
  return ONBOARDING_STEPS.filter((step) => state.steps[step].status !== 'pending').length;
}

export function Overview() {
  const { businessId = '', locationId: siteId } = useTenantParams();
  const signals = useOverviewSignals(businessId, siteId ?? '');
  const onboarding = useOnboarding(siteId ?? '');
  // Read once at mount (lazy initializer) — same run-once shape as
  // ClaimedBanner's own `visible` state, so this doesn't re-evaluate every
  // render as the signals hook resolves.
  const [arrival] = useState(() => readArrival());

  // CLAIM DOOR — see header comment. All hooks above are called
  // unconditionally before this early return, so this doesn't break the
  // rules of hooks.
  if (siteId && arrival.claimed && onboarding.kind === 'ready') {
    const incomplete = onboarding.state === null || onboarding.state.completedAt === null;
    if (incomplete) {
      return <Navigate to={`${locationRoutes.setup(businessId, siteId)}?claimed=1`} replace />;
    }
  }

  let body: ReactNode;
  if (signals.kind === 'error') {
    body = (
      <p role="alert" className="text-sm text-danger-fg">
        {signals.message}
      </p>
    );
  } else if (signals.kind !== 'ready' || !siteId) {
    // Honest empty frame while signals load — same "nothing thrown, nothing
    // faked" contract as PanelShell's own loading branch.
    body = <div className="min-h-[12rem]" aria-hidden="true" />;
  } else {
    const result = derivePath(businessId, siteId, signals.input);
    const onboardingState = onboarding.kind === 'ready' ? onboarding.state : null;
    let resumeChip: ReactNode = null;
    if (onboardingState && onboardingState.completedAt === null && result.mode === 'setup') {
      resumeChip = (
        <Link
          to={locationRoutes.setup(businessId, siteId)}
          className="rounded-lg border border-border-subtle bg-surface-sunken px-4 py-3 text-sm font-medium text-brand-700 hover:text-brand-800"
        >
          finish your setup — {stepsActedOnCount(onboardingState)} of 4 steps done
        </Link>
      );
    }
    body = (
      <>
        {resumeChip}
        {result.mode === 'setup' ? (
          <SetupChecklist result={result} />
        ) : (
          <WeeklyLayerRows siteId={siteId} result={result} />
        )}
      </>
    );
  }

  return (
    <PageContainer>
      <ClaimedBanner />
      {/* The overview had NO <h1> at all before the design audit (2026-08-28,
          A5) — the biggest text on the business's home screen was a checklist
          label. "Your business" is the eyebrow because this screen sits ABOVE
          the four layer sections rather than inside one of them; the business
          NAME stays in the sidebar switcher. */}
      <PageHeader eyebrow="Your business" title="Overview" />
      {arrival.draft ? (
        <p
          role="status"
          className="rounded-lg border border-border-subtle bg-surface-sunken px-4 py-3 text-sm text-text-secondary"
        >
          We're getting your preview ready.
        </p>
      ) : null}
      {body}
    </PageContainer>
  );
}

function SetupChecklist({ result }: { result: PathEngineResult }) {
  // Progress phrasing, not stepper phrasing (wp8 fix-forward list,
  // 2026-08-21): the old "step N of 4" heading read like a wizard position —
  // an owner who had JUST finished the 4-step setup wizard landed here and
  // saw "step 1 of 4" again, as if their progress was lost. These checklist
  // items are the path engine's live-signal setup TASKS, not wizard steps —
  // and the resume chip above can render in the same branch counting wizard
  // "steps done" from a different source, so this heading deliberately says
  // "tasks", not "steps", to keep the two adjacent figures from reading as
  // one contradictory metric (review finding, same date).
  return (
    <div className="space-y-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        setup tasks — {result.stepsDone} of {result.steps.length} done
      </p>
      <ul className="space-y-2">
        {result.steps.map((step) => (
          <li
            key={step.key}
            data-step={step.key}
            data-done={step.done}
            className="flex items-center justify-between gap-4 rounded-lg border bg-card px-4 py-3"
          >
            <span
              className={
                step.done
                  ? 'text-sm text-muted-foreground line-through'
                  : 'text-sm font-medium text-foreground'
              }
            >
              {step.label}
            </span>
            {step.done ? (
              // Real, audible text (task-6 review MEDIUM-2) — the previous
              // aria-hidden wrapper hid "done" from screen readers entirely,
              // so a completed step announced NOTHING distinguishing it from
              // an incomplete one.
              <span className="shrink-0 text-sm text-success-fg">
                <span aria-hidden="true">✓ </span>
                done
              </span>
            ) : (
              <span className="flex shrink-0 items-center gap-2">
                {/* sr-only sibling text (MEDIUM-2): mirrors "done" above so
                    both states are unambiguous to assistive tech, not just
                    inferred from the presence of a link. */}
                <span className="sr-only">not completed</span>
                {/* aria-label gives every step's link a distinct accessible
                    name (task-6 review MINOR-1) — sighted users still see the
                    identical "Go →" text; only the accessible name changes. */}
                {/* T11/A7: the hit area was 40x20 — under the 24 px
                    minimum vertically. Padding, not a bigger glyph. */}
                <Link
                  to={step.to}
                  aria-label={`Go to ${step.label}`}
                  className="inline-flex min-h-6 items-center px-1 text-sm font-semibold text-brand-700 hover:text-brand-800 pointer-coarse:min-h-11"
                >
                  Go →
                </Link>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function WeeklyLayerRows({ siteId, result }: { siteId: string; result: PathEngineResult }) {
  return (
    <div className="space-y-3">
      {result.layers.map((layer) => {
        const action = layer.action ? { label: layer.action.label, href: layer.action.to } : null;
        return (
          <LayerRow
            key={`${siteId}-${layer.layer}`}
            layer={layer.layer}
            status={LAYER_ROW_STATUS[layer.status]}
            summary={layer.headline}
            {...(action
              ? {
                  action,
                  renderAction: (a: { label: string; href: string }) => (
                    <Link
                      to={a.href}
                      className="shrink-0 text-sm font-semibold text-brand-700 hover:text-brand-800"
                    >
                      {a.label} →
                    </Link>
                  ),
                }
              : {})}
          />
        );
      })}
    </div>
  );
}
